import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PIC } from "@ryde/assets";
import { FramerTooltip } from "@ryde/controls";
import { getGridLabelKey, isEmptyFilterValue } from "@ryde/shared";

import { REPORT_FILTER_ENTITY } from "../dynamic-dashboard.constants";
import { IDashboardCard } from "../dynamic-dashboard.models";

interface IDashboardCardFilterBadgeProps {
  card: IDashboardCard;
  onClick: () => void;
}

/**
 * Marks a card as filtered without spending card space on the widgets: the
 * tooltip names which filters are active, and clicking opens the editor, which is
 * where a card's filters are changed. Renders nothing for an unfiltered card.
 *
 * Only filter names are listed, not their values — resolving ids to names would
 * mean loading every dropdown's items on the dashboard just to draw a tooltip.
 */
export const DashboardCardFilterBadge: FC<IDashboardCardFilterBadgeProps> = ({
  card,
  onClick,
}) => {
  const { t } = useTranslation();

  const activeFilterLabels = useMemo(
    () =>
      Object.entries(card.cardFilters ?? {})
        .filter(([, value]) => !isEmptyFilterValue(value))
        .map(([templateKey]) =>
          t(getGridLabelKey(REPORT_FILTER_ENTITY, templateKey)),
        ),
    [card.cardFilters, t],
  );

  if (!activeFilterLabels.length) {
    return null;
  }

  return (
    <FramerTooltip
      content={t("general.FILTERED_BY_ENTITY", {
        entity: activeFilterLabels.join(", "),
      })}
    >
      <button
        className="icon-btn"
        type="button"
        data-e2e="dashboard-card-filter-badge"
        aria-label={t("general.FILTERED_BY_ENTITY", {
          entity: activeFilterLabels.join(", "),
        })}
        onClick={onClick}
      >
        <PIC.FilterApplySvg />
      </button>
    </FramerTooltip>
  );
};
